import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PageLayoutFree from '../../../components/common/PageLayout/PageLayoutFree';
import '../../../styles/SelectCoin/SelectCoin.css';
import * as purchaseActions from '../../../actions/purchaseActions';
import { canceledOperation } from '../../../actions/authActions';
import { setStage } from '../../../actions/transactionActions';
import { routerActions } from 'connected-react-router';
import { SelectCoin as i18n } from '../../../middlewares/i18n'
import * as multicryptoActions from '../../../actions/multicryptoActions';
import LoadingAnimation from '../../../styles/assets/LoadingScreen_Symbol_F.webm';
import ButtonBS from '../../../components/common/ButtonBS';

import { track } from '../../../helpers/analytics';
import CoinCard from './CoinCard';

class SelectCoin extends Component {

  constructor(props) {
    super(props);
    this.state = {
      disabled: false,
      currentPage: 0
    }
    this.handleCoinSelect = this.handleCoinSelect.bind(this);
    this.handleNextPage = this.handleNextPage.bind(this);
    this.handlePrevPage = this.handlePrevPage.bind(this);
  }

  componentDidMount() {
    this.setState({
      disabled: false
    });
    this.props.actions.purchase.unlockExit();
    this.props.actions.multicrypto.getEnabledCoins();
    this.props.actions.setStage(1);
    if(!this.props.qa.qa_partial)
      this.props.actions.canceledOperation()
  }

  handleCoinSelect(coin) {
    if (this.state.disabled) return;

    // Find all variants (same ticker across different chains)
    const variants = this.props.multicrypto.enabledCoins.filter(c => (
      c.ticker === coin.ticker
    ));

    // If more than one chain is available, go to Select-Chain step
    if (variants.length > 1) {
      track('Navigated To Select Chain');
      this.props.actions.router.push('/select-chain', { variants });
      return;
    }

    // Otherwise keep current behaviour
    track('Coin Selected', {
      coin_type: coin.name,
      chain: coin.chain.name
    });
    this.setState({ disabled: true }, () => {
      this.props.actions.multicrypto.setSelectedCoin(coin);
    });
  }

  handleNextPage() {
    this.setState(prevState => ({
      currentPage: prevState.currentPage + 1
    }));
  }

  handlePrevPage() {
    this.setState(prevState => ({
      currentPage: Math.max(0, prevState.currentPage - 1)
    }));
  }

  render() {
    if(this.props.multicrypto.loadingCoins) {
      return (
        <div className="bs-body loading-cont">
          <div className='loading-vid'>
            <video controls={false} autoPlay loop width='800' height='800'>
              <source src={LoadingAnimation} type='video/webm'/>
            </video>
          </div>
          <h4 className="no-margin loading-mes">Loading coins<span>.</span><span>.</span><span>.</span></h4>
        </div>
      )
    }
    else {
      const allCoins = this.props.multicrypto.enabledCoins;

      // Remove duplicates: only one card per ticker (ignoring the test-net "T" prefix)
      const uniqueCoinsMap = new Map(); // key => coin
      allCoins.forEach(coin => {
        const tickerKey = coin.ticker;
        if (!uniqueCoinsMap.has(tickerKey)) {
          uniqueCoinsMap.set(tickerKey, coin);
        }
      });
      const uniqueCoins = Array.from(uniqueCoinsMap.values());

      // ----- Sort coins by market-cap and apply stable-coin positioning rules -----
      const COINS_PER_PAGE = 6;

      // Retrieve marketCap helper
      const getMarketCap = (coin) => {
        const rateInfo = this.props.rate?.multicrypto_rates?.[coin.name?.toLowerCase()] || {};
        // Support multiple possible property names just in case
        return rateInfo.marketCap ?? rateInfo.market_cap ?? coin.marketCap ?? coin.market_cap ?? 0;
      }

      // Separate stable coins and sort the rest by market-cap desc
      const STABLE_ORDER = ['USDT', 'USDC'];
      const normalizeTicker = (ticker) => {
        const tk = ticker.toUpperCase();
        return tk.startsWith('T') && tk.length > 3 ? tk.substring(1) : tk; // strip leading 'T' for testnet tickers
      };

      const stableCoins = STABLE_ORDER
        .map(ticker => uniqueCoins.find(c => normalizeTicker(c.ticker) === ticker))
        .filter(Boolean);

      const nonStableCoins = uniqueCoins.filter(c => !STABLE_ORDER.includes(normalizeTicker(c.ticker)));
      nonStableCoins.sort((a, b) => getMarketCap(b) - getMarketCap(a));

      // Position stable coins at bottom of first page (index 4 & 5 or just 5)
      const firstPageNonStableLimit = Math.max(0, COINS_PER_PAGE - stableCoins.length);
      const orderedCoins = [
        ...nonStableCoins.slice(0, firstPageNonStableLimit),
        ...stableCoins,
        ...nonStableCoins.slice(firstPageNonStableLimit)
      ];

      const startIndex = this.state.currentPage * COINS_PER_PAGE;
      const coinsToShow = orderedCoins.slice(startIndex, startIndex + COINS_PER_PAGE);
      const hasMoreCoins = startIndex + COINS_PER_PAGE < orderedCoins.length;
      const hasPrevCoins = this.state.currentPage > 0;

      return (
        <PageLayoutFree phone={false}>
          <div className="select-coin-header">
            <div className='bs-logo'/>
            <div className='line' />
            <h1 className="bs-title">{i18n[this.props.i18n.language.current].title}</h1>
          </div>
          <div className="coin-selection-container" key={this.state.currentPage}>
            {coinsToShow.map((coin) => {
              // Determine if this coin ticker has more than one variant (same ticker, different chain)
              const variantsCount = allCoins.filter(c => c.ticker === coin.ticker).length;
              const showChainLogo = coin.is_token && variantsCount === 1;

              return (
                <CoinCard
                  key={`${coin.ticker}-${this.state.currentPage}`}
                  coin={coin}
                  multicryptoRate={this.props.rate.multicrypto_rates[coin.name.toLowerCase()]}
                  logo={`/assets/coins/${coin.ticker.toUpperCase()}.png`}
                  handleCoinSelect={() => this.handleCoinSelect(coin)}
                  chainLogo={showChainLogo ? `/assets/chains/${coin.chain.name.toUpperCase()}.png` : null}
                />
              );
            })}
          </div>
          {orderedCoins.length > 6 && (
          <div className="pagination-controls">
            <ButtonBS
              content={i18n[this.props.i18n.language.current].prev}
              className='pagination-button-coin-cards'
              disabled={!hasPrevCoins}
              onClick={this.handlePrevPage}
            />
            <span>Page {this.state.currentPage + 1} of {Math.ceil(orderedCoins.length / COINS_PER_PAGE)}</span>
            <ButtonBS
              content={i18n[this.props.i18n.language.current].next}
              className='pagination-button-coin-cards'
              disabled={!hasMoreCoins}
              onClick={this.handleNextPage}
            />
          </div>
          )}
        </PageLayoutFree>
      )
    }
  }
}

function mapDispatchToProps(dispatch) {
  return {
    actions: {
      purchase: bindActionCreators(purchaseActions, dispatch),
      setStage: bindActionCreators(setStage, dispatch),
      canceledOperation: bindActionCreators(canceledOperation, dispatch),
      router: bindActionCreators(routerActions, dispatch),
      multicrypto: bindActionCreators(multicryptoActions, dispatch)
    }
  }
}

function mapStateToProps(state) {
  return {
    i18n: state.i18n,
    qa: state.qa,
    rate: state.rate,
    multicrypto: state.multicrypto
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SelectCoin);
